import type React from 'react'

// ─── Table display preferences (font size + density) ────────────────────────
// Canonical home for the font-size / density value sets so both the rendering
// layer (EnhancedTable / Table) and the preferences layer
// (features/table-columns) agree on the same vocabulary.

export type TableFontSize = 'sm' | 'md' | 'lg'
export type TableDensity = 'compact' | 'comfortable' | 'spacious'

export interface TableDisplayPref {
  fontSize: TableFontSize
  density: TableDensity
}

// `md` + `comfortable` reproduce the historical defaults (fontSize 14,
// padding 8px 4px 7px), so tables without a saved preference look unchanged.
export const DEFAULT_TABLE_DISPLAY: TableDisplayPref = {
  fontSize: 'md',
  density: 'comfortable'
}

export const FONT_SIZE_PX: Record<TableFontSize, number> = {
  sm: 12,
  md: 14,
  lg: 16
}

// Vertical cell padding (top / bottom). Horizontal padding stays constant (4px).
export const DENSITY_PADDING: Record<TableDensity, { pt: number; pb: number }> =
  {
    compact: { pt: 4, pb: 3 },
    comfortable: { pt: 8, pb: 7 },
    spacious: { pt: 14, pb: 13 }
  }

export const FONT_SIZE_OPTIONS: { value: TableFontSize; label: string }[] = [
  { value: 'sm', label: 'Pequena' },
  { value: 'md', label: 'Padrão' },
  { value: 'lg', label: 'Grande' }
]

export const DENSITY_OPTIONS: { value: TableDensity; label: string }[] = [
  { value: 'compact', label: 'Compacta' },
  { value: 'comfortable', label: 'Padrão' },
  { value: 'spacious', label: 'Confortável' }
]

export function isTableFontSize(v: unknown): v is TableFontSize {
  return v === 'sm' || v === 'md' || v === 'lg'
}

export function isTableDensity(v: unknown): v is TableDensity {
  return v === 'compact' || v === 'comfortable' || v === 'spacious'
}

// Icon scale factor relative to the `md` baseline (14px). Lets in-cell icons
// that can't inherit the cell font-size (MUI SvgIcons) grow/shrink together with
// the text. `md` resolves to 1 so the historical default stays pixel-identical.
export const ICON_SCALE: Record<TableFontSize, number> = {
  sm: FONT_SIZE_PX.sm / FONT_SIZE_PX.md,
  md: 1,
  lg: FONT_SIZE_PX.lg / FONT_SIZE_PX.md
}

/**
 * CSS custom properties consumed by `Table.styles` cells. Setting them on a
 * table ancestor (the scroll wrapper) cascades font-size / padding to every
 * cell without threading props through each one.
 */
export function tableDisplayToCssVars(
  display?: Partial<TableDisplayPref>
): React.CSSProperties {
  const rawFontSize = display?.fontSize
  const rawDensity = display?.density
  const fontSize = isTableFontSize(rawFontSize)
    ? rawFontSize
    : DEFAULT_TABLE_DISPLAY.fontSize
  const density = isTableDensity(rawDensity)
    ? rawDensity
    : DEFAULT_TABLE_DISPLAY.density
  const padding = DENSITY_PADDING[density]
  return {
    ['--cb-table-font-size' as any]: `${FONT_SIZE_PX[fontSize]}px`,
    ['--cb-table-cell-pt' as any]: `${padding.pt}px`,
    ['--cb-table-cell-pb' as any]: `${padding.pb}px`,
    ['--cb-table-icon-scale' as any]: `${ICON_SCALE[fontSize]}`
  }
}
